// Task block component for timeline display
// CREATED: Extracted from DayTimeline.tsx for reusability
// UPDATED: Uses TimelineContext to eliminate prop drilling (21 props -> 5)
// Computes position, drag state, conflict state, and tooltip positioning from context
// Handles adaptive display modes (compact/expanded) based on duration

import { useState, useCallback } from 'react';
import { Clock, Repeat, Check, AlertCircle } from 'lucide-react';
import { getServiceBackgroundClass, getServiceBorderClass, getServiceTextClass } from '../../../lib/services';
import { getStatusBadgeClass, getStatusIcon, getStatusLabel } from '../../../lib/statusUtils';
import { formatTimeRange } from '../../../lib/dateUtils';
import { timeToPixelOffset } from '../../../lib/timeCalculations';
import { useTimelineContext } from './TimelineContext';
import type { TimelineTask } from '../../../hooks/useDragDropTimeline';

interface TooltipPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

interface TimelineTaskBlockProps {
  task: TimelineTask;
  column: number;
  totalColumns: number;
  operatorId: string;
  isGhostPreview?: boolean;
}

function getDisplayMode(duration: number): 'compact' | 'expanded' {
  return duration >= 60 ? 'expanded' : 'compact';
}

function getExpandedLineClamp(height: number): number {
  if (height < 75) return 2;
  if (height < 100) return 3;
  return 4;
}

function renderStatusBadge(task: TimelineTask, totalColumns: number) {
  const statusIcon = getStatusIcon(task.status);
  const badgeClass = getStatusBadgeClass(task.status);
  const size = totalColumns > 2 ? 12 : 14;
  const iconSize = totalColumns > 2 ? 8 : 9;
  const IconComponent = statusIcon === 'check' ? Check : statusIcon === 'alert' ? AlertCircle : Clock;

  return (
    <div
      className={`absolute top-1 right-1 z-10 ${badgeClass} rounded-full border border-white shadow-md flex items-center justify-center`}
      style={{ width: `${size}px`, height: `${size}px` }}
      title={getStatusLabel(task.status)}
    >
      <IconComponent size={iconSize} className="text-white" />
    </div>
  );
}

function getTooltipClasses(position: TooltipPosition, isBeingDragged: boolean): string {
  let classes = 'absolute bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs whitespace-nowrap transition-opacity duration-200 pointer-events-none';

  if (isBeingDragged) {
    classes += ' !opacity-0';
  } else {
    classes += ' opacity-0 group-hover:opacity-100';
  }

  if (position.vertical === 'top') {
    classes += ' bottom-full mb-2';
  } else {
    classes += ' top-full mt-2';
  }

  if (position.horizontal === 'left') {
    classes += ' left-0';
  } else if (position.horizontal === 'right') {
    classes += ' right-0';
  } else {
    classes += ' left-1/2 -translate-x-1/2';
  }

  return classes;
}

function getTooltipArrowClasses(position: TooltipPosition): string {
  let classes = 'absolute w-0 h-0 border-l-4 border-r-4 border-transparent';

  if (position.vertical === 'top') {
    classes += ' top-full border-t-4 border-t-gray-900';
  } else {
    classes += ' bottom-full border-b-4 border-b-gray-900';
  }

  if (position.horizontal === 'left') {
    classes += ' left-4';
  } else if (position.horizontal === 'right') {
    classes += ' right-4';
  } else {
    classes += ' left-1/2 -translate-x-1/2';
  }

  return classes;
}

export function TimelineTaskBlock({
  task,
  column,
  totalColumns,
  operatorId,
  isGhostPreview = false
}: TimelineTaskBlockProps) {
  const {
    draggedTask,
    draggedTaskPosition,
    isDragRollback,
    isTouchDragging,
    conflictingTaskIds,
    scrollContainerRef,
    onDragStart,
    onDragEnd,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    onTaskClick,
    canReassignTask
  } = useTimelineContext();

  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    vertical: 'top',
    horizontal: 'center'
  });

  const isBeingDragged = draggedTask?.id === task.id;
  const isDraggable = !isGhostPreview && canReassignTask(task);
  const hasConflict = !isGhostPreview && conflictingTaskIds.has(task.id);

  const displayTime = isBeingDragged && draggedTaskPosition
    ? draggedTaskPosition.time
    : task.startTime;
  const top = timeToPixelOffset(displayTime);
  const rawHeight = task.duration;
  const height = Math.max(rawHeight - 1, 28);

  const taskColumnWidth = 100 / totalColumns;
  const leftPercent = column * taskColumnWidth;
  const widthPercent = taskColumnWidth;
  const gapSize = totalColumns > 1 ? 2 : 0;

  const serviceBackground = task.serviceColor
    ? getServiceBackgroundClass(task.serviceColor)
    : 'bg-gray-100';
  const serviceBorder = task.serviceColor
    ? getServiceBorderClass(task.serviceColor)
    : 'border-gray-300';
  const serviceText = task.serviceColor
    ? getServiceTextClass(task.serviceColor)
    : 'text-gray-800';

  const displayMode = getDisplayMode(task.duration);
  const baseSize = totalColumns > 2 ? 0.7 : 1;

  const calculateTooltipPosition = useCallback((element: HTMLElement): TooltipPosition => {
    const rect = element.getBoundingClientRect();
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return { vertical: 'top', horizontal: 'center' };

    const containerRect = scrollContainer.getBoundingClientRect();
    const relativeTop = rect.top - containerRect.top;
    const relativeLeft = rect.left - containerRect.left;
    const relativeRight = containerRect.right - rect.right;

    const vertical: 'top' | 'bottom' = relativeTop < 150 ? 'bottom' : 'top';
    let horizontal: 'left' | 'center' | 'right' = 'center';
    const EDGE_THRESHOLD = 120;
    if (relativeLeft < EDGE_THRESHOLD) horizontal = 'left';
    else if (relativeRight < EDGE_THRESHOLD) horizontal = 'right';

    return { vertical, horizontal };
  }, [scrollContainerRef]);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setTooltipPosition(calculateTooltipPosition(e.currentTarget));
  }, [calculateTooltipPosition]);

  const handleClick = useCallback(() => {
    if (!isTouchDragging && !isGhostPreview) {
      onTaskClick?.(task);
    }
  }, [isTouchDragging, isGhostPreview, onTaskClick, task]);

  const handleDragStartBound = useCallback((e: React.DragEvent) => {
    if (!isGhostPreview) onDragStart(e, task);
  }, [isGhostPreview, onDragStart, task]);

  const handleTouchStartBound = useCallback((e: React.TouchEvent) => {
    if (!isGhostPreview) onTouchStart(e, task, operatorId);
  }, [isGhostPreview, onTouchStart, task, operatorId]);

  const handleTouchMoveBound = useCallback((e: React.TouchEvent) => {
    if (isGhostPreview) return;
    const el = (e.currentTarget as HTMLElement).closest('[data-timeline-column]') as HTMLElement | null;
    onTouchMove(e, operatorId, el);
  }, [isGhostPreview, onTouchMove, operatorId]);

  const handleTouchEndBound = useCallback((e: React.TouchEvent) => {
    if (!isGhostPreview) onTouchEnd(e, operatorId);
  }, [isGhostPreview, onTouchEnd, operatorId]);

  const handleTouchCancelBound = useCallback((e: React.TouchEvent) => {
    if (!isGhostPreview) onTouchCancel(e);
  }, [isGhostPreview, onTouchCancel]);

  return (
    <div
      className={`absolute rounded-lg ${hasConflict ? 'border-2 border-red-500' : `border ${serviceBorder}`} ${serviceBackground} shadow-md transition-all group hover:z-10 ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      }`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `calc(${leftPercent}% + ${gapSize}px)`,
        width: `calc(${widthPercent}% - ${gapSize * 2}px)`,
        minHeight: '28px',
        overflow: 'visible',
        zIndex: isBeingDragged ? 50 : 'auto',
        transition: isDragRollback && isBeingDragged ? 'top 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'
      }}
      draggable={isDraggable}
      onDragStart={handleDragStartBound}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStartBound}
      onTouchMove={handleTouchMoveBound}
      onTouchEnd={handleTouchEndBound}
      onTouchCancel={handleTouchCancelBound}
    >
      {renderStatusBadge(task, totalColumns)}

      {displayMode === 'compact' && (
        <div className="flex items-center h-full px-1.5">
          <p
            className={`font-semibold ${serviceText} leading-tight truncate`}
            style={{ fontSize: `${0.6875 * baseSize}rem` }}
          >
            {task.title}
          </p>
        </div>
      )}

      {displayMode === 'expanded' && (
        <div className="flex flex-col h-full p-1.5 overflow-hidden">
          <div className={`${serviceText} opacity-75 mb-0.5 flex-shrink-0`}>
            <span
              className="font-medium whitespace-nowrap leading-tight"
              style={{ fontSize: `${0.5625 * baseSize}rem` }}
            >
              {formatTimeRange(displayTime, task.duration)}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <p
              className={`font-semibold ${serviceText} leading-tight break-words overflow-hidden`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: getExpandedLineClamp(rawHeight),
                WebkitBoxOrient: 'vertical',
                fontSize: `${0.75 * baseSize}rem`,
                lineHeight: '1.15'
              }}
            >
              {task.title}
            </p>
          </div>
        </div>
      )}

      <div className={getTooltipClasses(tooltipPosition, isBeingDragged)} style={{ zIndex: 9999 }}>
        <div className="font-semibold mb-1">{task.title}</div>
        <div className="text-gray-300">
          {formatTimeRange(displayTime, task.duration)}
        </div>
        <div className="text-gray-300">
          Statut: {getStatusLabel(task.status)}
        </div>
        {task.isRecurring && (
          <div className="text-gray-300 flex items-center gap-1 mt-1">
            <Repeat size={12} />
            <span>Tache recurrente</span>
          </div>
        )}
        {task.serviceName && (
          <div className="text-gray-300 mt-1">
            Service: <span className={`font-semibold ${getServiceTextClass(task.serviceColor || '')}`}>
              {task.serviceName}
            </span>
          </div>
        )}
        <div className={getTooltipArrowClasses(tooltipPosition)}></div>
      </div>
    </div>
  );
}
